import { LogUserActivity } from '@/apis/users/requests.ts'
import { Button } from '@/app/common/components/button/Button.tsx'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/common/components/Card.tsx'
import InceptionStatusBadge from '@/common/components/InceptionBadge.tsx'
import MultipleSelector from '@/common/components/MultipleSelector.tsx'
import Pagination from '@/common/components/Pagination.tsx'
import SearchInput from '@/common/components/SearchInput.tsx'
import ApplicationStatusBadge from '@/common/components/StatusBadge.tsx'
import StudentStatusBadge from '@/common/components/StudentStatusBadge.tsx'
import { LOG_TYPES } from '@/common/constants.ts'
import { CircleArrowsIcon } from '@/common/icons/arrows.tsx'
import { FilterIcon } from '@/common/icons/general.tsx'
import { cn, currencyFormatter } from '@/common/utilities.ts'
import { FamilyApplicationQueries } from '@/queries/family_applications/queries.ts'
import {
  FamilyApplication,
  FamilyApplicationList,
} from '@/queries/family_applications/requests.ts'
import { ProgramQueries } from '@/queries/program/queries.ts'
import { AppRoutePaths } from '@/routing/route_paths'
import { RootState } from '@/storage/store.ts'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { format, parseISO } from 'date-fns'
import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { DownIcon, UpIcon, ViewIcon } from '../../../common/icons/general'

// eslint-disable-next-line
export const TABLE_FILTERS = [
  { id: 'all', name: 'All' },
  { id: 1, name: 'Invited' },
  { id: 2, name: 'In Progress' },
  { id: 3, name: 'Pending' },
  { id: 4, name: 'Eligible' },
  { id: 5, name: 'In Review' },
  { id: 6, name: 'Ineligible' },
  { id: 7, name: 'Complete' },
  { id: 8, name: 'Awarded' },
  { id: 9, name: 'Appeal' },
  { id: 10, name: 'Funded' },
  { id: 11, name: 'Wait Listed' },
  { id: 16, name: 'Withdrawn' },
  { id: '4_2', name: 'Selected' },
]

const columnLabelValDic: Record<string, string> = {
  Applicant: 'applicant_name',
  'Applicant Name': 'applicant_name',
  Student: 'student_name',
  'Sequence Number': 'student_sequence_number',
  'Award Amount': 'total_awarded_amount',
  ID: 'applicant_id',
  Status: 'status',
  Type: 'inception_status',
  'Last Updated': 'last_datetime',
}

function getFullName(
  firstName?: string | null,
  lastName?: string | null
): string {
  return `${firstName ?? ''} ${lastName ?? ''}`
}

const columnHelper = createColumnHelper<FamilyApplication>()

const THREE_MINUTES = 1000 * 60 * 3

const InitialApplications: FamilyApplicationList = [] as const
const RECORDS_PER_PAGE = 10 as const
type ListItem = { id: number | string; name: string }
export default function ApplicantsListTable() {
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedFilter, setSelectedFilter] = useState<ListItem[]>([
    { id: 'all', name: 'All' },
  ])
  const [search, setsearch] = useState('')
  const [recordsPerPage, setRecordsPerPage] = useState<number>(RECORDS_PER_PAGE)
  const { selectedProgramYear } = useSelector((s: RootState) => s.programs)
  const [sort_by, setSortBy] = useState<undefined | string>('last_datetime')
  const [sort_order, setSortOrder] = useState<'asc' | 'desc'>('asc')

  // Prefetching here so that we have data in memory if user views an application's details
  useQuery(ProgramQueries.getProgramById(selectedProgramYear?.id))
  const ApplicationListQuery = useQuery(
    FamilyApplicationQueries.getPaginatedListByProgramId(
      recordsPerPage,
      currentPage,
      selectedProgramYear?.id,
      search,
      selectedFilter.map((filter) => filter.id).join(','),
      sort_by,
      sort_order,
      undefined,
      {
        enabled:
          !!selectedProgramYear &&
          (search !== '' ||
            selectedFilter.length > 0 ||
            selectedFilter.some((filter) => filter.id === 'all')),
        placeholderData: keepPreviousData,
        refetchInterval: THREE_MINUTES,
      }
    )
  )

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePerPageChange = (perPage: number) => {
    setRecordsPerPage(perPage)
  }

  const refetchApplicationsList = () =>
    ApplicationListQuery.refetch({ throwOnError: true, cancelRefetch: false })

  useEffect(() => {
    setCurrentPage(1)
  }, [search])

  useEffect(() => {
    if (ApplicationListQuery.data === undefined) {
      setCurrentPage(1)
    }
  }, [ApplicationListQuery.data, currentPage])

  const hasLoggedPageView = useRef(false)
  useEffect(() => {
    if (!hasLoggedPageView.current) {
      LogUserActivity({
        log_type: LOG_TYPES.VIEW_PAGE,
        suffix_text: 'Applicants',
      }) // Log page view
      hasLoggedPageView.current = true // Mark as executed
    }
  }, [])

  const sortBy = (column: string) => {
    const colVar = columnLabelValDic[column]
    const newOrder =
      colVar == sort_by ? (sort_order == 'desc' ? 'asc' : 'desc') : 'asc'
    setSortOrder(newOrder)
    setSortBy(colVar)
  }

  const headerForColumn = (name: string) => {
    const colVar = columnLabelValDic[name]
    return (
      <span
        className="cursor-pointer"
        onClick={() => {
          sortBy(name)
        }}
      >
        {name}{' '}
        {colVar == sort_by ? (
          sort_order == 'asc' ? (
            <UpIcon />
          ) : (
            <DownIcon />
          )
        ) : (
          ''
        )}{' '}
      </span>
    )
  }

  const columns = [
    columnHelper.accessor(
      (row) => getFullName(row.applicant_first_name, row.applicant_last_name),
      {
        id: 'full_name',
        header: () => (
          <>
            <span className="block pl-4 2xl:hidden">
              {headerForColumn('Applicant')}
            </span>
            <span className="hidden pl-4 2xl:block">
              {headerForColumn('Applicant Name')}
            </span>
          </>
        ),
        cell: (info) => {
          const row = info.row.original
          return (
            <div className="flex items-center">
              <div className="w-3/4 sm:w-full font-bold pl-4">
                <div className="font-medium text-gray-900 flex justify-between items-center">
                  <span>
                    {' '}
                    {getFullName(
                      row.applicant_first_name,
                      row.applicant_last_name
                    )}{' '}
                  </span>
                  <ApplicationStatusBadge
                    status={row.status}
                    className="md:hidden"
                  />
                </div>
                <div className="mt-2 space-y-1 text-gray-500 font-normal md:hidden">
                  <div>
                    {getFullName(row.student_first_name, row.student_last_name)}
                  </div>
                </div>
              </div>
            </div>
          )
        },
      }
    ),
    columnHelper.accessor('applicant_id', {
      id: 'applicant_id',
      header: () => headerForColumn('Applicant ID'),
      cell: (info) => <div>{info.getValue()}</div>,
    }),
    columnHelper.accessor(
      (row) => getFullName(row.student_first_name, row.student_last_name),
      {
        id: 'student_full_name',
        header: () => headerForColumn('Student'),
        cell: (info) => {
          if (info.row.original.student_id) {
            return (
              <div>
                {info.getValue()}{' '}
                <strong>{`(ID: ${info.row.original.student_id})`}</strong>
              </div>
            )
          }
          return <div>{info.getValue()}</div>
        },
      }
    ),

    columnHelper.accessor('student_sequence_number', {
      id: 'student_sequence_number',
      header: () => <span>Sequence Number</span>,
      cell: (info) => <span>{info.getValue()}</span>,
    }),

    columnHelper.accessor('total_awarded_amount', {
      id: 'total_awarded_amount',
      header: () => <span>Award Amount</span>,
      cell: (info) => {
        const value = info.getValue()
        return value != null ? currencyFormatter.format(value) : ''
      },
    }),

    columnHelper.accessor(
      (row) => ({
        status: row.status,
        subStatus: row.sub_status,
      }),
      {
        id: 'status',
        header: () => headerForColumn('Status'),
        cell: (info) => {
          const values = info.getValue() // This gets the object with status and subStatus
          return (
            <div>
              <StudentStatusBadge
                status={values.status}
                subStatus={values.subStatus}
              />
            </div>
          )
        },
      }
    ),
    columnHelper.accessor('inception_status', {
      id: 'inception_status',
      header: () => headerForColumn('Type'),
      cell: (info) => <InceptionStatusBadge status={info.getValue()} />,
    }),
    columnHelper.accessor('last_datetime', {
      header: () => headerForColumn('Last Updated'),
      // header: () => <span>Last Updated</span>,
      cell: (info) => {
        const lastDateTimeStr = info.getValue()
        return (
          <span>
            {lastDateTimeStr !== null
              ? format(parseISO(lastDateTimeStr), 'MM/dd/yyyy, h:mm aa')
              : ''}
          </span>
        )
      },
    }),
    columnHelper.display({
      id: 'action',
      header: () => <span className="pr-2 2xl:p-1"></span>,
      cell: (info) => {
        const row = info.row.original
        return (
          <Link
            to={AppRoutePaths.applicants.applicant_details.application.replace(
              ':applicantId',
              `${row.id}`
            )}
          >
            <ViewIcon className="text-primary" />
          </Link>
          // <ActionDropdown application={row} />
        )
      },
    }),
  ]

  const tableData =
    ApplicationListQuery.isFetching || (search === '' && !selectedFilter.length)
      ? InitialApplications
      : (ApplicationListQuery.data?.data ?? InitialApplications)
  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: ApplicationListQuery.data?.last_page,
  })

  return (
    <>
      <section aria-labelledby="tasks-list-title">
        <Card className="mt-6">
          <CardHeader className="flex justify-between items-center text-md font-semibold">
            <div className="text-left text-lg md:text-md text-gray-900">
              Applicants
              <span className="hidden mt-1 w-15ch text-sm font-normal text-gray-400 lg-sm:block lg-sm:w-full">
                {ApplicationListQuery.isFetching
                  ? 'refreshing data...'
                  : ApplicationListQuery.data === undefined
                    ? 'last updated at: ' + format(new Date(), 'hh:mm:ss a')
                    : 'last updated at: ' +
                      format(ApplicationListQuery.dataUpdatedAt, 'hh:mm:ss a')}
              </span>
            </div>
            <div>
              <div className="space-y-3 flex flex-col items-start lg-sm:flex-row lg-sm:items-center lg-sm:space-y-0 lg-sm:space-x-4">
                <SearchInput
                  id="table_search"
                  placeholder="Search Applicants"
                  value={search}
                  onChange={(e) => setsearch(e.target.value)}
                />
                <MultipleSelector
                  list={TABLE_FILTERS.sort((a, b) =>
                    a.name.localeCompare(b.name)
                  )}
                  selected={selectedFilter}
                  setSelected={setSelectedFilter}
                  icon={<FilterIcon className="text-primary" />}
                />
                <div className="flex space-x-2 lg-sm:flex-col lg-sm:space-x-0">
                  <Button
                    variant="outline"
                    className="max-w-12"
                    size="md"
                    disabled={ApplicationListQuery.isFetching}
                    onClick={refetchApplicationsList}
                  >
                    <CircleArrowsIcon className="text-base" />
                  </Button>
                  <span className="block w-15ch text-sm font-normal text-gray-400 lg-sm:w-full lg-sm:hidden">
                    {ApplicationListQuery.isFetching
                      ? 'refreshing data...'
                      : 'last updated at: ' +
                        format(
                          ApplicationListQuery.dataUpdatedAt,
                          'hh:mm:ss a'
                        )}
                  </span>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="flex flex-col p-0 lg:p-0 h-full w-full">
            <table
              className={
                tableData.length === 0 &&
                search === '' &&
                selectedFilter.length === 0
                  ? 'mt-5'
                  : ''
              }
            >
              <thead>
                {tableData.length === 0 &&
                search === '' &&
                selectedFilter.length === 0 ? (
                  <tr>
                    <th colSpan={table.getAllColumns().length}>
                      Search Applicants by Applicant Name, Student Name, Email
                      or by Filter
                    </th>
                  </tr>
                ) : (
                  table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header, index) => (
                        <th
                          key={header.id}
                          className={cn(
                            'w-[16%] px-2 py-3.5 text-left text-sm font-semibold text-gray-900 md:py-4 md:px-2.5',
                            index != 0 &&
                              index != headerGroup.headers.length - 1 &&
                              'hidden md:table-cell',
                            index == headerGroup.headers.length - 1 &&
                              'text-left'
                          )}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </th>
                      ))}
                    </tr>
                  ))
                )}
              </thead>
              {(function () {
                if (ApplicationListQuery.error) {
                  return (
                    <tr>
                      <td colSpan={5} className="text-center p-2">
                        {ApplicationListQuery.error.message}
                      </td>
                    </tr>
                  )
                } else {
                  return (
                    <tbody>
                      {table.getRowModel().rows.map((row) => (
                        <tr key={row.id}>
                          {row.getVisibleCells().map((cell, index) => (
                            <td
                              key={cell.id}
                              className={cn(
                                'w-[16%] py-4 px-2 text-sm font-sans text-left text-gray-500 border-t border-gray-200 md:py-4 md:px-2',
                                index != 0 &&
                                  index != row.getVisibleCells().length - 1 &&
                                  'hidden md:table-cell',
                                index == row.getVisibleCells().length - 1 &&
                                  'text-left'
                              )}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  )
                }
              })()}
            </table>
          </CardContent>
          <CardFooter className="p-0 border-t border-transparent mt-5">
            <Pagination
              itemsPerPage={recordsPerPage}
              currentPage={currentPage}
              totalRows={
                search || selectedFilter.length > 0
                  ? (ApplicationListQuery.data?.total ?? 0)
                  : 0
              }
              onPageChange={handlePageChange}
              onPerPageChange={handlePerPageChange}
            />
          </CardFooter>
        </Card>
      </section>
    </>
  )
}
